import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  TextField,
  InputAdornment,
  Skeleton,
} from '@mui/material';
import {
  Home as HomeIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { GET_NODES_LIST } from '../../graphql/queries/nodes.queries';
import { GET_CLARIFICATION_SCHEMAS_LIST } from '../../graphql/queries/clarificationSchemas.queries';
import {
  GET_CHATBOT_SECTION_NODES,
  GET_CHATBOT_SECTION_SCHEMAS,
  GET_CHATBOT_DOCUMENTS,
  GET_CHATBOT_SECTION_DOCUMENTS,
} from '../../graphql/queries/chatbotSectionConfig.queries';
import { GET_CHATBOT_SECTION } from '../../graphql/queries/chatbotSections.queries';
import {
  ASSIGN_SECTION_NODES,
  ASSIGN_SECTION_SCHEMAS,
  ASSIGN_SECTION_DOCUMENTS,
} from '../../graphql/mutations/chatbotSectionConfig.mutations';
import {
  NodesListResponse,
  FindNodeListInput,
  Node,
} from '../../types/nodes.types';
import {
  ClarificationSchemaListResponse,
  FindClarificationSchemaListInput,
  ClarificationSchema,
} from '../../types/clarificationSchemas.types';
import {
  ChatbotSectionResponse,
  FindChatbotSectionInput,
} from '../../types/chatbotSections.types';
import MainLayout from '../../components/Layout/MainLayout';
import './ChatbotSectionConfig.css';

interface SectionRelationResponse<T> {
  success: boolean;
  message: string;
  errors: string[];
  data: T[] | null;
}

interface AssignResponse {
  success: boolean;
  message: string;
  errors: string[];
}

interface ChatbotDocument {
  id: string;
  tenantId: string;
  userId: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  path: string;
  uri: string;
  description: string | null;
  tags: string[];
  docMetadata: any;
  documentTypeId: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface ChatbotDocumentsListData {
  documents: ChatbotDocument[];
  hasMorePages: boolean;
  nextPage: number | null;
  totalPages: number;
  totalRecords: number;
}

interface ChatbotDocumentsListResponse {
  success: boolean;
  message: string;
  errors: string[];
  data: ChatbotDocumentsListData | null;
}

const ChatbotSectionConfig: React.FC = () => {
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId: string }>();

  const [activeTab, setActiveTab] = useState(0);
  const [sectionName, setSectionName] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Nodes tab state
  const [allNodes, setAllNodes] = useState<Node[]>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [nodesLoadingState, setNodesLoadingState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [savingNodes, setSavingNodes] = useState(false);
  const [nodeSearch, setNodeSearch] = useState('');

  // Schemas tab state
  const [allSchemas, setAllSchemas] = useState<ClarificationSchema[]>([]);
  const [selectedSchemaIds, setSelectedSchemaIds] = useState<Set<string>>(new Set());
  const [schemasLoadingState, setSchemasLoadingState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [savingSchemas, setSavingSchemas] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState('');

  // Documents tab state
  const [allDocuments, setAllDocuments] = useState<ChatbotDocument[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [documentsLoadingState, setDocumentsLoadingState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [savingDocuments, setSavingDocuments] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');

  // Queries
  const [fetchSection] = useLazyQuery<
    { chatbotSection: ChatbotSectionResponse },
    { params: FindChatbotSectionInput }
  >(GET_CHATBOT_SECTION, { fetchPolicy: 'network-only' });

  const [fetchAllNodes] = useLazyQuery<
    { nodes: NodesListResponse },
    { params: FindNodeListInput }
  >(GET_NODES_LIST, { fetchPolicy: 'network-only' });

  const [fetchSectionNodes] = useLazyQuery<
    { chatbotSectionNodes: SectionRelationResponse<Node> },
    { params: { sectionId: string } }
  >(GET_CHATBOT_SECTION_NODES, { fetchPolicy: 'network-only' });

  const [fetchAllSchemas] = useLazyQuery<
    { clarificationSchemas: ClarificationSchemaListResponse },
    { params: FindClarificationSchemaListInput }
  >(GET_CLARIFICATION_SCHEMAS_LIST, { fetchPolicy: 'network-only' });

  const [fetchSectionSchemas] = useLazyQuery<
    { chatbotSectionSchemas: SectionRelationResponse<ClarificationSchema> },
    { params: { sectionId: string } }
  >(GET_CHATBOT_SECTION_SCHEMAS, { fetchPolicy: 'network-only' });

  const [fetchAllDocuments] = useLazyQuery<
    { chatbotDocuments: ChatbotDocumentsListResponse },
    { params: any }
  >(GET_CHATBOT_DOCUMENTS, { fetchPolicy: 'network-only' });

  const [fetchSectionDocuments] = useLazyQuery<
    { chatbotSectionDocuments: SectionRelationResponse<ChatbotDocument> },
    { params: { sectionId: string } }
  >(GET_CHATBOT_SECTION_DOCUMENTS, { fetchPolicy: 'network-only' });

  // Mutations
  const [assignNodesMutation] = useMutation<
    { assignSectionNodes: AssignResponse },
    { params: { sectionId: string; nodeIds: string[] } }
  >(ASSIGN_SECTION_NODES);

  const [assignSchemasMutation] = useMutation<
    { assignSectionSchemas: AssignResponse },
    { params: { sectionId: string; schemaIds: string[] } }
  >(ASSIGN_SECTION_SCHEMAS);

  const [assignDocumentsMutation] = useMutation<
    { assignSectionDocuments: AssignResponse },
    { params: { sectionId: string; documentIds: string[] } }
  >(ASSIGN_SECTION_DOCUMENTS);

  // Load section name and nodes on mount
  useEffect(() => {
    if (sectionId) {
      loadSectionName();
      setNodesLoadingState('loading');
      loadNodesData();
    }
  }, [sectionId]);

  // Load schemas when tab switches for the first time
  useEffect(() => {
    if (activeTab === 1 && schemasLoadingState === 'idle' && sectionId) {
      setSchemasLoadingState('loading');
      loadSchemasData();
    }
    if (activeTab === 2 && documentsLoadingState === 'idle' && sectionId) {
      setDocumentsLoadingState('loading');
      loadDocumentsData();
    }
  }, [activeTab]);

  const loadSectionName = async () => {
    try {
      const { data } = await fetchSection({ variables: { params: { ids: [sectionId!] } } });
      if (data?.chatbotSection?.success && data.chatbotSection.data && data.chatbotSection.data.length > 0) {
        setSectionName(data.chatbotSection.data[0].name);
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
    }
  };

  // ===== Nodes Data =====
  const loadNodesData = async () => {
    try {
      const [nodesResult, assignedResult] = await Promise.all([
        fetchAllNodes({
          variables: {
            params: {
              search: null,
              page: 1,
              pageSize: 1000,
              sort: { field: 'order', order: 'asc' },
            } as any,
          },
        }),
        fetchSectionNodes({
          variables: { params: { sectionId: sectionId! } },
        }),
      ]);

      if (nodesResult.data?.nodes?.success && nodesResult.data.nodes.data) {
        setAllNodes(nodesResult.data.nodes.data.nodes);
      } else {
        setError(nodesResult.data?.nodes?.message || 'Error al cargar nodos');
      }

      if (assignedResult.data?.chatbotSectionNodes?.success && assignedResult.data.chatbotSectionNodes.data) {
        const assignedIds = new Set(assignedResult.data.chatbotSectionNodes.data.map((n) => n.id));
        setSelectedNodeIds(assignedIds);
      } else if (assignedResult.data?.chatbotSectionNodes?.success === false) {
        setError(assignedResult.data?.chatbotSectionNodes?.message || 'Error al cargar nodos asignados');
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      setError(err.message || 'Error de conexión');
    } finally {
      setNodesLoadingState('done');
    }
  };

  const handleToggleNode = (nodeId: string) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
      return next;
    });
  };

  const handleAssignNodes = async () => {
    if (!sectionId) return;
    try {
      setSavingNodes(true);
      setError(null);
      const { data: responseData } = await assignNodesMutation({
        variables: { params: { sectionId, nodeIds: Array.from(selectedNodeIds) } },
      });
      if (responseData?.assignSectionNodes?.success) {
        setSuccessMessage('Nodos asignados exitosamente');
      } else {
        const msg = responseData?.assignSectionNodes?.message || 'Error al asignar nodos';
        const errs = responseData?.assignSectionNodes?.errors || [];
        setError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSavingNodes(false);
    }
  };

  // ===== Schemas Data =====
  const loadSchemasData = async () => {
    try {
      const [schemasResult, assignedResult] = await Promise.all([
        fetchAllSchemas({
          variables: {
            params: {
              search: null,
              page: 1,
              pageSize: 1000,
              sort: { field: 'entity', order: 'asc' },
            },
          },
        }),
        fetchSectionSchemas({
          variables: { params: { sectionId: sectionId! } },
        }),
      ]);

      if (schemasResult.data?.clarificationSchemas?.success && schemasResult.data.clarificationSchemas.data) {
        setAllSchemas(schemasResult.data.clarificationSchemas.data.schemas);
      } else {
        setError(schemasResult.data?.clarificationSchemas?.message || 'Error al cargar esquemas');
      }

      if (assignedResult.data?.chatbotSectionSchemas?.success && assignedResult.data.chatbotSectionSchemas.data) {
        const assignedIds = new Set(assignedResult.data.chatbotSectionSchemas.data.map((s) => s.id));
        setSelectedSchemaIds(assignedIds);
      } else if (assignedResult.data?.chatbotSectionSchemas?.success === false) {
        setError(assignedResult.data?.chatbotSectionSchemas?.message || 'Error al cargar esquemas asignados');
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      setError(err.message || 'Error de conexión');
    } finally {
      setSchemasLoadingState('done');
    }
  };

  const handleToggleSchema = (schemaId: string) => {
    setSelectedSchemaIds((prev) => {
      const next = new Set(prev);
      next.has(schemaId) ? next.delete(schemaId) : next.add(schemaId);
      return next;
    });
  };

  const handleAssignSchemas = async () => {
    if (!sectionId) return;
    try {
      setSavingSchemas(true);
      setError(null);
      const { data: responseData } = await assignSchemasMutation({
        variables: { params: { sectionId, schemaIds: Array.from(selectedSchemaIds) } },
      });
      if (responseData?.assignSectionSchemas?.success) {
        setSuccessMessage('Esquemas asignados exitosamente');
      } else {
        const msg = responseData?.assignSectionSchemas?.message || 'Error al asignar esquemas';
        const errs = responseData?.assignSectionSchemas?.errors || [];
        setError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSavingSchemas(false);
    }
  };

  // ===== Documents Data =====
  const loadDocumentsData = async () => {
    try {
      const [docsResult, assignedResult] = await Promise.all([
        fetchAllDocuments({
          variables: {
            params: {
              search: null,
              page: 1,
              pageSize: 1000,
              sort: { field: 'createdAt', order: 'desc' },
            },
          },
        }),
        fetchSectionDocuments({
          variables: { params: { sectionId: sectionId! } },
        }),
      ]);

      if (docsResult.data?.chatbotDocuments?.success && docsResult.data.chatbotDocuments.data) {
        setAllDocuments(docsResult.data.chatbotDocuments.data.documents);
      } else {
        setError(docsResult.data?.chatbotDocuments?.message || 'Error al cargar documentos');
      }

      if (assignedResult.data?.chatbotSectionDocuments?.success && assignedResult.data.chatbotSectionDocuments.data) {
        const assignedIds = new Set(assignedResult.data.chatbotSectionDocuments.data.map((d) => d.id));
        setSelectedDocumentIds(assignedIds);
      } else if (assignedResult.data?.chatbotSectionDocuments?.success === false) {
        setError(assignedResult.data?.chatbotSectionDocuments?.message || 'Error al cargar documentos asignados');
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      setError(err.message || 'Error de conexión');
    } finally {
      setDocumentsLoadingState('done');
    }
  };

  const handleToggleDocument = (docId: string) => {
    setSelectedDocumentIds((prev) => {
      const next = new Set(prev);
      next.has(docId) ? next.delete(docId) : next.add(docId);
      return next;
    });
  };

  const handleAssignDocuments = async () => {
    if (!sectionId) return;
    try {
      setSavingDocuments(true);
      setError(null);
      const { data: responseData } = await assignDocumentsMutation({
        variables: { params: { sectionId, documentIds: Array.from(selectedDocumentIds) } },
      });
      if (responseData?.assignSectionDocuments?.success) {
        setSuccessMessage('Documentos asignados exitosamente');
      } else {
        const msg = responseData?.assignSectionDocuments?.message || 'Error al asignar documentos';
        const errs = responseData?.assignSectionDocuments?.errors || [];
        setError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSavingDocuments(false);
    }
  };

  // Filters
  const filteredNodes = nodeSearch
    ? allNodes.filter(
        (n) =>
          n.name.toLowerCase().includes(nodeSearch.toLowerCase()) ||
          n.code.toLowerCase().includes(nodeSearch.toLowerCase())
      )
    : allNodes;

  const filteredSchemas = schemaSearch
    ? allSchemas.filter(
        (s) =>
          s.entity.toLowerCase().includes(schemaSearch.toLowerCase()) ||
          s.description.toLowerCase().includes(schemaSearch.toLowerCase())
      )
    : allSchemas;

  const filteredDocuments = documentSearch
    ? allDocuments.filter(
        (d) =>
          d.fileName.toLowerCase().includes(documentSearch.toLowerCase()) ||
          (d.description || '').toLowerCase().includes(documentSearch.toLowerCase())
      )
    : allDocuments;

  const isLoadingNodesTab = nodesLoadingState !== 'done';
  const isLoadingSchemasTab = schemasLoadingState !== 'done';
  const isLoadingDocumentsTab = documentsLoadingState !== 'done';

  // Skeleton placeholder
  const renderSkeletonGrid = () => (
    <div className="nodes-grid">
      {Array.from({ length: 12 }).map((_, idx) => (
        <Box key={idx} className="node-card" sx={{ pointerEvents: 'none' }}>
          <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 1, mr: 1.5, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={22} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        </Box>
      ))}
    </div>
  );

  // Reusable tab content renderer
  const renderTabContent = <T extends { id: string }>(config: {
    isLoading: boolean;
    searchValue: string;
    onSearchChange: (val: string) => void;
    searchPlaceholder: string;
    selectedIds: Set<string>;
    selectedLabel: string;
    saving: boolean;
    onAssign: () => void;
    assignLabel: string;
    items: T[];
    onToggle: (id: string) => void;
    renderName: (item: T) => string;
    renderSub: (item: T) => string;
    emptyMessage: string;
  }) => {
    if (config.isLoading) return renderSkeletonGrid();
    return (
      <>
        <Box className="section-config-toolbar">
          <Box className="search-box">
            <TextField
              size="small"
              placeholder={config.searchPlaceholder}
              value={config.searchValue}
              onChange={(e) => config.onSearchChange(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {config.selectedIds.size} {config.selectedLabel}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={config.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={config.onAssign}
            disabled={config.saving}
            sx={{
              backgroundColor: '#667eea',
              '&:hover': { backgroundColor: '#5568d3' },
            }}
          >
            {config.saving ? 'Guardando...' : config.assignLabel}
          </Button>
        </Box>

        <div className="nodes-grid">
          {config.items.map((item) => {
            const isSelected = config.selectedIds.has(item.id);
            return (
              <div
                key={item.id}
                className={`node-card ${isSelected ? 'selected' : ''}`}
                onClick={() => config.onToggle(item.id)}
              >
                <Checkbox
                  checked={isSelected}
                  size="small"
                  sx={{
                    mr: 1,
                    color: '#667eea',
                    '&.Mui-checked': { color: '#667eea' },
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => config.onToggle(item.id)}
                />
                <Box>
                  <div className="node-card-name">{config.renderName(item)}</div>
                  <div className="node-card-code">{config.renderSub(item)}</div>
                </Box>
              </div>
            );
          })}
          {config.items.length === 0 && (
            <Typography color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
              {config.emptyMessage}
            </Typography>
          )}
        </div>
      </>
    );
  };

  return (
    <MainLayout>
      <div className="section-config-container">
        <Paper elevation={2} className="section-config-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Link
              underline="hover"
              sx={{ cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/chatbot-sections')}
            >
              Secciones Chatbot
            </Link>
            <Typography color="text.primary">Configuración</Typography>
          </Breadcrumbs>

          <Box className="section-config-header">
            <Typography variant="h4" component="h1">
              Configuración de Sección
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sectionName ? `Sección: ${sectionName}` : 'Cargando...'}
            </Typography>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Nodos" />
            <Tab label="Esquemas de Clarificación" />
            <Tab label="Documentos" />
          </Tabs>

          {/* ===== TAB: Nodos ===== */}
          {activeTab === 0 && (
            <Box>
              {renderTabContent<Node>({
                isLoading: isLoadingNodesTab,
                searchValue: nodeSearch,
                onSearchChange: setNodeSearch,
                searchPlaceholder: 'Filtrar nodos...',
                selectedIds: selectedNodeIds,
                selectedLabel: 'nodo(s) seleccionado(s)',
                saving: savingNodes,
                onAssign: handleAssignNodes,
                assignLabel: 'Asignar Nodos',
                items: filteredNodes,
                onToggle: handleToggleNode,
                renderName: (n) => n.name,
                renderSub: (n) => n.code,
                emptyMessage: 'No se encontraron nodos',
              })}
            </Box>
          )}

          {/* ===== TAB: Esquemas de Clarificación ===== */}
          {activeTab === 1 && (
            <Box>
              {renderTabContent<ClarificationSchema>({
                isLoading: isLoadingSchemasTab,
                searchValue: schemaSearch,
                onSearchChange: setSchemaSearch,
                searchPlaceholder: 'Filtrar esquemas...',
                selectedIds: selectedSchemaIds,
                selectedLabel: 'esquema(s) seleccionado(s)',
                saving: savingSchemas,
                onAssign: handleAssignSchemas,
                assignLabel: 'Asignar Esquemas',
                items: filteredSchemas,
                onToggle: handleToggleSchema,
                renderName: (s) => s.entity,
                renderSub: (s) => s.description,
                emptyMessage: 'No se encontraron esquemas',
              })}
            </Box>
          )}

          {/* ===== TAB: Documentos ===== */}
          {activeTab === 2 && (
            <Box>
              {renderTabContent<ChatbotDocument>({
                isLoading: isLoadingDocumentsTab,
                searchValue: documentSearch,
                onSearchChange: setDocumentSearch,
                searchPlaceholder: 'Filtrar documentos...',
                selectedIds: selectedDocumentIds,
                selectedLabel: 'documento(s) seleccionado(s)',
                saving: savingDocuments,
                onAssign: handleAssignDocuments,
                assignLabel: 'Asignar Documentos',
                items: filteredDocuments,
                onToggle: handleToggleDocument,
                renderName: (d) => d.fileName,
                renderSub: (d) => d.description || '',
                emptyMessage: 'No se encontraron documentos',
              })}
            </Box>
          )}
        </Paper>

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default ChatbotSectionConfig;
